import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  Sparkles,
  XCircle,
} from "lucide-react";

/* ====================================================== */
/* COMPONENT                                              */
/* ====================================================== */

function UpgradeToProModal({
  open,
  onClose,
  onConfirm,

  loading = false,
  status = "idle",
  error = null,

  pricing = null,

  billingComplete = true,
}) {

  /* ==================================================== */
  /* STATE                                                */
  /* ==================================================== */

  const [promoCode, setPromoCode] =
    useState("");

  /* ==================================================== */
  /* RESET                                                */
  /* ==================================================== */

  useEffect(() => {

    if (!open) {
      setPromoCode("");
    }

  }, [open]);

  /* ==================================================== */
  /* DERIVED                                              */
  /* ==================================================== */

  const isBusy =
    loading ||
    [
      "creating_order",
      "opening_checkout",
      "waiting_for_activation",
      "payment_success",
    ].includes(status);

  const canClose =
    !isBusy;

  const baseAmount =
    pricing?.baseAmount ||
    999;

  const finalAmount =
    pricing?.finalAmount ||
    baseAmount;

  const discount =
    pricing?.discount ||
    0;

  const hasDiscount =
    discount > 0;

  const primaryButtonLabel =
    useMemo(() => {

      switch (status) {

        case "creating_order":
          return "Creating Order...";

        case "opening_checkout":
          return "Opening Checkout...";

        case "payment_success":
          return "Payment Successful";

        case "waiting_for_activation":
          return "Activating Plan...";

        case "activated":
          return "Activated";

        default:
          return "Proceed to Payment";

      }

    }, [status]);

  /* ==================================================== */
  /* HANDLERS                                             */
  /* ==================================================== */

  const handleConfirm =
    () => {

      if (isBusy) {
        return;
      }

      onConfirm?.({
        promoCode:
          promoCode.trim() ||
          null,
      });

    };

  /* ==================================================== */
  /* EARLY RETURN                                         */
  /* ==================================================== */

  if (!open) {
    return null;
  }

  /* ==================================================== */
  /* RENDER                                               */
  /* ==================================================== */

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm">

      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl">

        {/* ================================================= */}
        {/* HEADER                                            */}
        {/* ================================================= */}

        <div className="border-b border-gray-100 p-6">

          <div className="mb-4 flex items-center gap-3">

            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-100">

              <Sparkles className="h-6 w-6 text-orange-600" />

            </div>

            <div>

              <h2 className="text-xl font-semibold text-gray-900">
                Upgrade to Pro
              </h2>

              <p className="text-sm text-gray-500">
                Unlock premium features for your team
              </p>

            </div>

          </div>

          {/* =============================================== */}
          {/* STATUS BANNER                                   */}
          {/* =============================================== */}

          {status === "waiting_for_activation" && (
            <Banner
              type="info"
              text="Payment received successfully. Activating your subscription..."
            />
          )}

          {status === "payment_success" && (
            <Banner
              type="success"
              text="Payment successful. Finalizing activation..."
            />
          )}

          {status === "activated" && (
            <Banner
              type="success"
              text="Your Pro plan is now active."
            />
          )}

          {!billingComplete && (
            <Banner
              type="warning"
              text="Billing profile is incomplete. Payment will continue normally."
            />
          )}

          {error && (
            <Banner
              type="error"
              text={error}
            />
          )}

        </div>

        {/* ================================================= */}
        {/* BODY                                              */}
        {/* ================================================= */}

        <div className="p-6">

          {/* =============================================== */}
          {/* PLAN CARD                                       */}
          {/* =============================================== */}

          <div className="rounded-2xl border border-gray-200 p-5">

            <div className="mb-5 flex items-start justify-between gap-4">

              <div>

                <div className="mb-1 text-lg font-semibold text-gray-900">
                  HeadwaySpot Pro
                </div>

                <p className="text-sm text-gray-500">
                  Advanced workflow & collaboration suite
                </p>

              </div>

              <div className="text-right">

                {hasDiscount && (
                  <div className="text-sm text-gray-400 line-through">
                    ₹{baseAmount}
                  </div>
                )}

                <div className="text-3xl font-bold text-gray-900">
                  ₹{finalAmount}
                </div>

                <div className="text-xs text-gray-500">
                  per month
                </div>

              </div>

            </div>

            {/* =========================================== */}
            {/* DISCOUNT                                    */}
            {/* =========================================== */}

            {hasDiscount && (
              <div className="mb-4 rounded-xl border border-green-100 bg-green-50 px-3 py-2 text-sm font-medium text-green-700">
                🎉 Promo applied — You saved ₹{discount}
              </div>
            )}

            {/* =========================================== */}
            {/* FEATURES                                    */}
            {/* =========================================== */}

            <div className="space-y-3">

              <Feature text="Unlimited teams & members" />
              <Feature text="Advanced analytics & reporting" />
              <Feature text="Priority support" />
              <Feature text="Future integrations access" />
              <Feature text="Premium collaboration tools" />

            </div>

            {/* =========================================== */}
            {/* PROMO                                       */}
            {/* =========================================== */}

            <div className="mt-5">

              <label className="mb-2 block text-sm font-medium text-gray-700">
                Promo Code
              </label>

              <input
                type="text"
                value={promoCode}
                disabled={isBusy}
                onChange={(e) => {
                  setPromoCode(
                    e.target.value
                  );
                }}
                placeholder="Enter promo code"
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100 disabled:cursor-not-allowed disabled:bg-gray-100"
              />

            </div>

          </div>

          {/* =============================================== */}
          {/* SECURITY NOTE                                   */}
          {/* =============================================== */}

          <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">

            <ShieldCheck className="h-4 w-4" />

            <span>
              Secured by Razorpay. Payments are encrypted and secure.
            </span>

          </div>

        </div>

        {/* ================================================= */}
        {/* FOOTER                                            */}
        {/* ================================================= */}

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 p-6">

          <button
            onClick={onClose}
            disabled={!canClose}
            className="rounded-xl border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Close
          </button>

          <button
            onClick={handleConfirm}
            disabled={isBusy}
            className="flex min-w-[190px] items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
          >

            {isBusy && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}

            {primaryButtonLabel}

          </button>

        </div>

      </div>

    </div>
  );
}

/* ====================================================== */
/* FEATURE                                                */
/* ====================================================== */

function Feature({
  text,
}) {

  return (
    <div className="flex items-center gap-3 text-sm text-gray-700">

      <CheckCircle2 className="h-4 w-4 text-green-600" />

      <span>
        {text}
      </span>

    </div>
  );

}

/* ====================================================== */
/* BANNER                                                 */
/* ====================================================== */

function Banner({
  type = "info",
  text,
}) {

  const config = {

    info: {
      bg:
        "bg-blue-50",
      border:
        "border-blue-100",
      text:
        "text-blue-700",
      icon:
        <Loader2 className="h-4 w-4 animate-spin" />,
    },

    success: {
      bg:
        "bg-green-50",
      border:
        "border-green-100",
      text:
        "text-green-700",
      icon:
        <CheckCircle2 className="h-4 w-4" />,
    },

    warning: {
      bg:
        "bg-amber-50",
      border:
        "border-amber-100",
      text:
        "text-amber-700",
      icon:
        <AlertCircle className="h-4 w-4" />,
    },

    error: {
      bg:
        "bg-red-50",
      border:
        "border-red-100",
      text:
        "text-red-700",
      icon:
        <XCircle className="h-4 w-4" />,
    },

  };

  const current =
    config[type] ||
    config.info;

  return (
    <div
      className={`mt-4 flex items-start gap-2 rounded-xl border p-3 text-sm ${current.bg} ${current.border} ${current.text}`}
    >

      <div className="mt-0.5">
        {current.icon}
      </div>

      <div>
        {text}
      </div>

    </div>
  );

}

export default UpgradeToProModal;